import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Image, TouchableOpacity, Text, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { BottomNavBar } from '../../components/BottomNavBar';

const categories = [
  { id: 'all', name: 'All Data', icon: require('../../assets/icons/all-tasks.png'), count: 0 },
  { id: 'personal', name: 'Personal Data', icon: require('../../assets/icons/personal.png'), count: 0 },
  { id: 'medical', name: 'Medical Data', icon: require('../../assets/icons/medical.png'), count: 0 },
  { id: 'finance', name: 'Finance Data', icon: require('../../assets/icons/financial.png'), count: 0 },
  { id: 'property', name: 'Property Data', icon: require('../../assets/icons/property.png'), count: 0 },
  { id: 'finalWishes', name: 'Final Wishes', icon: require('../../assets/icons/final-wishes.png'), count: 0 },
  { id: 'finalCare', name: 'Final Care', icon: require('../../assets/icons/final-care.png'), count: 0 },
  { id: 'passwords', name: 'Passwords', icon: require('../../assets/icons/password.png'), count: 0 },
  { id: 'contacts', name: 'Contacts', icon: require('../../assets/icons/all-contacts.png'), count: 0 },
  { id: 'expenses', name: 'Expenses', icon: require('../../assets/icons/expenses.png'), count: 0 },
  { id: 'documents', name: 'Documents', icon: require('../../assets/icons/folder.png'), count: 0 },
  { id: 'loyaltyPoints', name: 'Loyalty Points', icon: require('../../assets/icons/loyalty.png'), count: 0 },
];

const FINANCIAL_SECTIONS = [
  {
    id: 'accountant',
    title: 'Accountant',
    fields: [
      'Full Name', 'Practice Name', 'Office Phone', 'Cell Phone', 'Email', 'Street Address', 'City', 'State', 'Zip Code', 'Special Instructions'
    ]
  },
  {
    id: 'attorney',
    title: 'Attorney',
    fields: [
      'Full Name', 'Practice Name', 'Office Phone', 'Cell Phone', 'Email', 'Street Address', 'City', 'State', 'Zip Code', 'Special Instructions'
    ]
  },
  {
    id: 'investmentAdvisor',
    title: 'Investment Advisor',
    fields: [
      "Advisor's Full Name", 'Firm Name', 'Office Phone Number', 'Cell Phone', 'Email Address', 'Street Address', 'City', 'State', 'Zip Code', 'Special Instructions', 'Investments handled by your advisor', 'Services your investment advisor provides'
    ]
  },
  {
    id: 'insuranceAgent',
    title: 'Insurance Agent',
    fields: [
      'Full Name', 'Agency Name', 'Office Phone', 'Cell Phone', 'Email', 'Street Address', 'City', 'State', 'Zip Code', 'Special Instructions'
    ]
  },
  {
    id: 'taxAdvisor',
    title: 'Tax Advisor',
    fields: [
      'Full Name', 'Practice Name', 'Office Phone', 'Cell Phone', 'Email', 'Street Address', 'City', 'State', 'Zip Code', 'Special Instructions'
    ]
  },
  {
    id: 'creditCards',
    title: 'Credit Cards',
    fields: [
      'Card Name', 'Card Number', 'Expiration Date', 'CVV', 'Issuer', 'Customer Service Phone', 'Online Account Username', 'Online Account Password', 'Notes'
    ]
  },
  {
    id: 'banking',
    title: 'Banking',
    fields: [
      'Bank Name', 'Account Type', 'Account Number', 'Routing Number', 'Branch Address', 'Branch Phone', 'Online Account Username', 'Online Account Password', 'Notes'
    ]
  },
  {
    id: 'investmentAccounts',
    title: 'Investment Accounts',
    fields: [
      'Account Name', 'Account Number', 'Institution', 'Account Type', 'Beneficiaries', 'Notes'
    ]
  },
  {
    id: 'loans',
    title: 'Loans',
    fields: [
      'Loan Type', 'Lender Name', 'Account Number', 'Loan Amount', 'Interest Rate', 'Monthly Payment', 'Maturity Date', 'Notes'
    ]
  },
  {
    id: 'crypto',
    title: 'Crypto Currency',
    fields: [
      'Wallet Name', 'Wallet Address', 'Currency Type', 'Exchange', 'Account Username', 'Account Password', 'Notes'
    ]
  }
];

type RootStackParamList = {
  AllData: { viewOnly?: boolean } | undefined;
  PersonalData: { viewOnly?: boolean } | undefined;
  MedicalData: { viewOnly?: boolean } | undefined;
  FinancialData: { viewOnly?: boolean } | undefined;
  PropertyData: { viewOnly?: boolean } | undefined;
  FinalWishesData: { viewOnly?: boolean } | undefined;
  FinalCareData: { viewOnly?: boolean } | undefined;
  OnlineAccountsData: { viewOnly?: boolean } | undefined;
  EmergencyContactsData: { viewOnly?: boolean } | undefined;
  ExpensesData: { viewOnly?: boolean } | undefined;
  DocumentManagementData: { viewOnly?: boolean } | undefined;
  LoyaltyPointsData: { viewOnly?: boolean } | undefined;
  PasswordsDashboardScreen: { viewOnly?: boolean } | undefined;
  ContactsDashboardScreen: { viewOnly?: boolean } | undefined;
  ExpensesDashboardScreen: { viewOnly?: boolean } | undefined;
  DocumentsDashboardScreen: { viewOnly?: boolean } | undefined;
  LoyaltyPointDashboardScreen: { viewOnly?: boolean } | undefined;
};
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface CategoryCardProps {
  item: {
    id: string;
    name: string;
    icon: any;
    count: number;
  };
  isSelected: boolean;
  onPress: (id: string) => void;
  viewOnly?: boolean;
}

const CATEGORY_CHECKLIST_SCREENS: Record<string, string> = {
  all: 'AllTasksChecklist',
  personal: 'PersonalChecklist',
  medical: 'MedicalChecklist',
  finance: 'FinanceChecklist',
  property: 'PropertyChecklist',
  finalWishes: 'FinalWishesChecklist',
  finalCare: 'FinalCareChecklist',
  passwords: '', // Add if exists
  contacts: 'EmergencyContactsChecklist',
  expenses: 'ExpensesChecklist',
  documents: 'DocumentManagementChecklist',
  loyaltyPoints: '', // Add if exists
};

const CategoryCard: React.FC<CategoryCardProps> = ({ item, isSelected, onPress, viewOnly }) => {
  const navigation = useNavigation<NavigationProp>();

  const handleCardPress = () => {
    onPress(item.id);
    switch (item.id) {
      case 'all':
        navigation.navigate({ name: 'AllData', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'personal':
        navigation.navigate({ name: 'PersonalData', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'medical':
        navigation.navigate({ name: 'MedicalData', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'finance':
        navigation.navigate({ name: 'FinancialData', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'property':
        navigation.navigate({ name: 'PropertyData', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'finalWishes':
        navigation.navigate({ name: 'FinalWishesData', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'finalCare':
        navigation.navigate({ name: 'FinalCareData', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'passwords':
        navigation.navigate({ name: 'PasswordsDashboardScreen', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'contacts':
        navigation.navigate({ name: 'ContactsDashboardScreen', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'expenses':
        navigation.navigate({ name: 'ExpensesDashboardScreen', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'documents':
        navigation.navigate({ name: 'DocumentsDashboardScreen', params: viewOnly ? { viewOnly: true } : undefined });
        break;
      case 'loyaltyPoints':
        navigation.navigate({ name: 'LoyaltyPointDashboardScreen', params: viewOnly ? { viewOnly: true } : undefined });
        break;
    }
  };

  const handleTasksPress = () => {
    const screen = CATEGORY_CHECKLIST_SCREENS[item.id];
    if (screen) {
      navigation.navigate(screen as any);
    }
  };

  return (
    <TouchableOpacity
      style={[
        styles.categoryCard,
        { backgroundColor: isSelected ? '#6A89A7' : '#FFFFFF', height: viewOnly ? s(100) : s(150) }
      ]}
      onPress={handleCardPress}
    >
      <View style={styles.categoryIconContainer}>
        <Image source={item.icon} style={styles.categoryIcon} />
      </View>
      <Text style={[
        styles.categoryName,
        { color: isSelected ? '#FFFFFF' : '#1C2C4F' }
      ]}>{item.name}</Text>
      {!viewOnly && (
        <TouchableOpacity style={styles.tasksButtonContainer} onPress={handleTasksPress} activeOpacity={0.8}>
          <Text style={styles.tasksButtonText}>Tasks</Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

const SECTION_INPUT_SCREENS: Record<string, string> = {
  accountant: 'AccountantInfoInput',
  attorney: 'AttorneyInfoInput',
  investmentAdvisor: 'InvestmentAdvisorInfoInput',
  insuranceAgent: 'InsuranceAgentInfoInput',
  taxAdvisor: 'TaxAdvisorInfoInput',
  creditCards: 'CreditCardInfoInput',
  banking: 'BankingInfoInput',
  investmentAccounts: 'InvestmentsInfoInput',
  loans: 'LoanInfoInput',
  crypto: 'CryptoInfoInput',
};

const SectionDataItem: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    {value && <Text style={styles.value}>{value}</Text>}
    <View style={styles.separator} />
  </View>
);

export const FinancialDataScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute();
  const viewOnly = (route.params as { viewOnly?: boolean } | undefined)?.viewOnly === true;
  const [selectedCategory, setSelectedCategory] = useState('finance');
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const scrollRef = React.useRef<ScrollView>(null);

  const handleCategoryPress = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const updatedCategories = categories.map(category => ({
    ...category,
    count: 0
  }));

  useEffect(() => {
    const cardWidth = 130 + 15;
    const paddingLeft = 20;
    const selectedIdx = updatedCategories.findIndex(cat => cat.id === selectedCategory);
    if (scrollRef.current && selectedIdx !== -1) {
      const screenWidth = Dimensions.get('window').width;
      const offset = Math.max(0, (cardWidth * selectedIdx + paddingLeft) - (screenWidth / 2) + (cardWidth / 2));
      scrollRef.current.scrollTo({ x: offset, animated: true });
    }
  }, [selectedCategory]);

  const toggleSection = (id: string) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../assets/icons/notification.png')}
              style={styles.bellIcon}
            />
          </TouchableOpacity>
        </View>
        {viewOnly && (
          <View style={{ backgroundColor: '#FF9F5A', padding: ms(8), alignItems: 'center', margin: ms(10), borderRadius: ms(8) }}>
            <Text style={{ color: '#fff', fontWeight: 'bold' }}>View Only Mode: You are viewing as a Successor/Keyholder</Text>
          </View>
        )}
        <ScrollView
          horizontal
          ref={scrollRef}
          showsHorizontalScrollIndicator={false}
          style={styles.categoriesList}
          contentContainerStyle={{ paddingRight: ms(30) }}
        >
          {updatedCategories.map((item) => (
            <CategoryCard
              key={item.id}
              item={item}
              isSelected={selectedCategory === item.id}
              onPress={handleCategoryPress}
              viewOnly={viewOnly}
            />
          ))}
        </ScrollView>
        <Text style={styles.title}>Financial EOL Data</Text>
        <ScrollView style={styles.sectionsScroll} contentContainerStyle={styles.sectionsContainer} showsVerticalScrollIndicator={false}>
          {FINANCIAL_SECTIONS.map(section => {
            const expanded = expandedSections.includes(section.id);
            return (
              <TouchableOpacity
                key={section.id}
                style={styles.sectionContainer}
                onPress={() => toggleSection(section.id)}
                activeOpacity={0.9}
              >
                <View style={styles.sectionHeaderRow}>
                  <Text style={styles.sectionTitle}>{section.title}</Text>
                  <Image
                    source={require('../../assets/icons/down.png')}
                    style={[
                      styles.sectionArrow,
                      expanded && { transform: [{ rotate: '180deg' }] }
                    ]}
                  />
                </View>
                {expanded && (
                  <View style={styles.sectionContent}>
                    {section.fields.map((field: string, idx: number) => (
                      <SectionDataItem key={idx} label={field} />
                    ))}
                    {SECTION_INPUT_SCREENS[section.id] && !viewOnly && (
                      <TouchableOpacity
                        style={styles.editDataButton}
                        onPress={() => navigation.navigate(SECTION_INPUT_SCREENS[section.id] as any)}
                      >
                        <Text style={styles.editDataButtonText}>Edit Data</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(25),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '10%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
    zIndex: 1,
  },
  bellIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    marginBottom: ms(10),
    marginTop: ms(-400),
    marginHorizontal: ms(15),
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  categoriesList: {
    paddingLeft: ms(20),
    marginBottom: 0,
    height: undefined,
    borderWidth: 0,
  },
  categoryCard: {
    width: s(130),
    height: s(150),
    borderRadius: ms(15),
    padding: ms(12),
    marginRight: ms(15),
    marginVertical: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  categoryIconContainer: {
    width: s(40),
    height: s(40),
    borderRadius: ms(20),
    backgroundColor: '#F3F6F8',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#29BFC0',
  },
  categoryName: {
    fontSize: ms(14, 0.3),
    fontWeight: '500',
    textAlign: 'center',
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6A89A7',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: 2,
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
    paddingVertical: ms(2),
    paddingHorizontal: 0,
    marginTop: ms(8),
  },
  sectionTitle: {
    color: '#FF9F5A',
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    marginBottom: ms(-2),
    marginTop: ms(2),
  },
  sectionArrow: {
    width: s(22),
    height: s(18),
    tintColor: '#29BFC0',
    marginLeft: ms(8),
    transform: [{ rotate: '0deg' }],
  },
  sectionContent: {
    paddingLeft: ms(8),
    paddingTop: ms(4),
  },
  editDataButton: {
    alignSelf: 'flex-end',
    backgroundColor: '#6A89A7',
    borderRadius: ms(15),
    paddingVertical: ms(6),
    paddingHorizontal: ms(16),
    marginTop: ms(10),
    marginBottom: ms(4),
  },
  editDataButtonText: {
    color: '#fff',
    fontSize: ms(14, 0.3),
    fontWeight: 'bold',
  },
  sectionsScroll: {
    flex: 1,
    marginTop: 0,
  },
  sectionsContainer: {
    paddingHorizontal: ms(20),
    paddingBottom: ms(200),
  },
  sectionContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(10),
    marginBottom: ms(10),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: ms(10),
  },
  tasksButtonContainer: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(10),
    paddingVertical: ms(3),
    paddingHorizontal: ms(12),
    alignSelf: 'center',
    marginTop: ms(6),
  },
  tasksButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: ms(12, 0.3),
    textAlign: 'center',
  },
});

